import React from 'react';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail,
  Edit2,
  Crown,
  Phone,
  ShieldCheck,
  ExternalLink,
  Globe
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './UserProfile.module.scss';
import InstagramBasicProfileData from './InstagramBasicProfileData';
import { beginInstagramConnect } from '../../utils/instagramConnect';
import Avatar from '../common/Avatar';

const UserProfile = ({ userData }) => {
  const { t, i18n } = useTranslation('profile');
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  if (!userData) {
    return (
      <div className="onboarding-loading-screen">
        <div className="luxury-spinner" />
        <p>{t('view.loading')}</p>
      </div>
    );
  }

  const isSelf = currentUser?.id === userData.id;
  const isOwner = isSelf || currentUser?.is_staff;
  const isCreator = userData.user_type === 'creator';
  const profile = isCreator ? userData.creator_profile : userData.business_profile;
  const isInstagramConnected = Boolean(profile?.instagram_account_connected);
  const displayName = isCreator
    ? [userData.first_name, userData.last_name].filter(Boolean).join(' ') || profile?.full_name || userData.username
    : profile?.company_name || userData.username;
  const displayPhone = userData.phone_number
    ? (userData.phone_number.startsWith('+') ? userData.phone_number : `+${userData.phone_number}`)
    : '';

  const handleEditProfile = () => navigate('/edit-profile');
  const handleInstagramConnect = () => {
    beginInstagramConnect(`${window.location.pathname}${window.location.search || ''}`);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
  };

  const hasMetricValue = (value) => value !== null && value !== undefined && value !== '';

  const formatNumber = (num) => {
    return new Intl.NumberFormat(i18n.language || 'en-US', { 
        notation: "compact", 
        compactDisplay: "short" 
    }).format(num);
  };

  const formatMetric = (num) => {
    if (!hasMetricValue(num)) return '—';
    return formatNumber(Number(num) || 0);
  };

  return (
    <div className={styles.profilePage}>
      {/* ── CINEMATIC HERO ── */}
      <div className={styles.editorialHero}>
        <div className={styles.profileCover} aria-hidden="true" />

        <motion.div 
          className={styles.avatarSection}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className={styles.avatarWrapper}>
            <Avatar
              size="xl"
              src={userData.display_image_url || userData.profile_picture_url || userData.profile_picture}
              fallbackSrc={profile?.ig_profile_picture_url}
              name={displayName}
              alt={displayName}
              className={styles.profileAvatar}
            />
            {profile?.admin_verified && (
              <div className={styles.statusBadge} title={t('view.verified')}>
                <Crown size={20} />
              </div>
            )}
          </div>
        </motion.div>

        <motion.h1 
          className={styles.profileName}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {displayName}
        </motion.h1>

        <motion.div 
          className={styles.profileHandle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {isCreator ? `@${userData.username}` : t('labels.business')}
        </motion.div>

        {isOwner && (
          <motion.div 
            className={styles.heroActions}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <button className={styles.btnEdit} onClick={handleEditProfile}>
              <Edit2 size={16} /> {t('view.edit_profile')}
            </button>
          </motion.div>
        )}
      </div>

      {/* ── STATS BAR ── */}
      <motion.div 
        className={`${styles.statsBar} ${!isInstagramConnected ? styles.statsBarSingle : ''}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        {isInstagramConnected && (
          <>
            <div className={styles.statItem}>
              <span className={styles.statValue}>
                {formatMetric(isCreator ? profile?.ig_followers : profile?.instagram_followers)}
              </span>
              <span className={styles.statLabel}>{t('instagram.followers')}</span>
            </div>

            <div className={styles.statItem}>
              <span className={styles.statValue}>
                {formatMetric(profile?.ig_media_count)}
              </span>
              <span className={styles.statLabel}>{t('instagram.posts')}</span>
            </div>

            <div className={styles.statDivider} />
          </>
        )}

        <div className={styles.statItem}>
          <span className={styles.statValue}>
            {userData.country_details?.name || 'Global'}
          </span>
          <span className={styles.statLabel}>{t('labels.location')}</span>
        </div>
      </motion.div>

      {/* ── CONTENT GRID ── */}
      <motion.div 
        className={styles.profileGrid}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className={styles.mainColumn}>
          <motion.section className={styles.profileSection} variants={itemVariants}>
            <h2 className={styles.sectionTitle}>
               {isCreator ? t('sections.about_creator') : t('sections.about_company')}
            </h2>
            <p className={styles.bioText}>
              {profile?.description || profile?.ig_biography || (isCreator ? t('empty.no_info_creator') : t('empty.no_description_business'))}
            </p>
          </motion.section>

          <InstagramBasicProfileData
            profile={profile}
            isCreator={isCreator}
            canManageConnection={isSelf}
            onConnect={handleInstagramConnect}
          />

          {(isCreator ? profile?.content_categories : profile?.business_categories)?.length > 0 && (
            <motion.section className={styles.profileSection} variants={itemVariants}>
              <h2 className={styles.sectionTitle}>
                {isCreator ? t('sections.categories_creator') : t('sections.categories_business')}
              </h2>
              <div className={styles.categoriesList}>
                {(isCreator ? profile.content_categories : profile.business_categories).map(cat => (
                  <span key={cat.id} className={styles.categoryTag}>{cat.name}</span>
                ))}
              </div>
            </motion.section>
          )}
        </div>

        <div className={styles.sideColumn}>
          <motion.div className={styles.infoCard} variants={itemVariants}>
            <h3 className={styles.cardTitle}>{t('sections.official_info')}</h3>
            <div className={styles.cardDetails}>
              {userData.email && isOwner && (
                <div className={styles.detailItem}>
                  <div className={styles.detailIcon}><Mail size={18} /></div>
                  <div className={styles.detailText}>
                    <span className={styles.label}>{t('labels.email')}</span>
                    <span className={styles.value}>{userData.email}</span>
                  </div>
                </div>
              )}

              {userData.phone_number && isOwner && (
                <div className={styles.detailItem}>
                  <div className={styles.detailIcon}><Phone size={18} /></div>
                  <div className={styles.detailText}>
                    <span className={styles.label}>{t('labels.phone')}</span>
                    <span className={styles.value}>{displayPhone}</span>
                  </div>
                </div>
              )}

              {(profile?.website || profile?.ig_website) && (
                <div className={styles.detailItem}>
                  <div className={styles.detailIcon}><Globe size={18} /></div>
                  <div className={styles.detailText}>
                    <span className={styles.label}>{t('labels.website')}</span>
                    <a href={profile?.website || profile?.ig_website} target="_blank" rel="noopener noreferrer" className={styles.websiteLink}>
                      {(profile?.website || profile?.ig_website).replace(/^https?:\/\//, '')}
                      <ExternalLink size={12} style={{ marginLeft: 4, opacity: 0.6 }} />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div className={styles.infoCard} variants={itemVariants}>
            <h3 className={styles.cardTitle}>{t('sections.status')}</h3>
            <div className={styles.cardDetails}>
                <div className={styles.detailItem}>
                    <div className={styles.detailIcon}><ShieldCheck size={18} /></div>
                    <div className={styles.detailText}>
                        <span className={styles.label}>{t('labels.account_tier')}</span>
                        <span className={styles.value} style={{ color: '#c9a227', fontWeight: 700 }}>
                            {profile?.admin_verified ? t('status.elite') : t('status.curated')}
                        </span>
                    </div>
                </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default UserProfile;
